import { ChangeDetectorRef, Component, Inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { ApiServiceInterface, ToastService, UserServiceInterface } from '@hutsix/ngxh6';

/**
 * Admin-only "download this table" button.
 *
 * Two modes:
 *  - rawUrl set (charts): fetches the RAW records behind the chart from the
 *    API ({rows: [...]}) and serialises those — client decision 2026-07-27:
 *    chart exports must contain the underlying records so the aggregates can
 *    be audited, not the aggregate rows themselves. sortBy pre-sorts by the
 *    chart's grouping dimension so the spreadsheet arrives grouped.
 *  - rows set (tables that already display raw rows): serialises the
 *    in-memory array as before.
 *
 * Only scalar columns are exported — nested arrays/objects are skipped.
 * Renders nothing for non-admins.
 *
 * Usage:
 *   <app-data-export-button [rows]="cat.items" [filename]="cat.name"></app-data-export-button>
 *   <app-data-export-button rawUrl="/api/incidents/export" sortBy="location" filename="incidents-by-location"></app-data-export-button>
 */
@Component({
    selector: 'app-data-export-button',
    templateUrl: './data-export-button.component.html',
    styleUrls: ['./data-export-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class DataExportButtonComponent {
    /** The rows backing this table/card (in-memory mode). */
    @Input() rows: Array<Record<string, any>> | null = null;
    /** API endpoint returning {rows: [...]} of raw records (raw mode; wins over rows). */
    @Input() rawUrl: string | null = null;
    /** Column to sort raw rows by before export — the chart's grouping dimension. */
    @Input() sortBy: string | null = null;
    /** Base filename (no extension); the date is appended. */
    @Input() filename = 'export';
    @Input() label = 'CSV';

    fetching = false;

    constructor(
        @Inject('UserService') private userService: UserServiceInterface,
        @Inject('ApiService') private api: ApiServiceInterface,
        private toastr: ToastService,
        private cdRef: ChangeDetectorRef,
    ) {}

    /** Data export is an admin-only capability. */
    get isAdmin(): boolean {
        return this.userService.isGranted('ROLE_ADMIN');
    }

    download(): void {
        if (this.rawUrl) {
            this.downloadRaw(this.rawUrl);
            return;
        }
        this.serialise(this.rows ?? []);
    }

    private downloadRaw(url: string): void {
        if (this.fetching) return;
        this.fetching = true;
        this.cdRef.markForCheck();
        this.api.get({ url, useCache: false }).subscribe(
            (res: any) => {
                // Host pages are all OnPush — without markForCheck the button
                // would stay disabled/spinning after the download completes
                // and could never be clicked again.
                this.fetching = false;
                this.cdRef.markForCheck();
                let rows: Array<Record<string, any>> = res?.data?.rows ?? [];
                if (this.sortBy) {
                    const key = this.sortBy;
                    rows = [...rows].sort((a, b) => String(a?.[key] ?? '').localeCompare(String(b?.[key] ?? '')));
                }
                this.serialise(rows);
            },
            () => {
                this.fetching = false;
                this.cdRef.markForCheck();
                this.toastr.error('Could not fetch the raw data for this export.');
            },
        );
    }

    private serialise(input: Array<Record<string, any>>): void {
        const rows = input.filter(r => r && typeof r === 'object');
        if (!rows.length) {
            this.toastr.info('Nothing to download for this table.');
            return;
        }
        const stamp = new Date().toISOString().slice(0, 10);
        this.downloadCsv(`${this.slug(this.filename)}-${stamp}.csv`, this.rowsToCsv(rows));
    }

    /** Serialise rows to RFC-4180 CSV; columns = union of scalar keys across rows. */
    private rowsToCsv(rows: Array<Record<string, any>>): string {
        const isScalar = (v: any) => v === null || ['string', 'number', 'boolean'].includes(typeof v);
        const headers: string[] = [];
        for (const row of rows) {
            for (const key of Object.keys(row)) {
                if (isScalar(row[key]) && !headers.includes(key)) headers.push(key);
            }
        }
        const escape = (v: any): string => {
            if (v === null || v === undefined) return '';
            let s = String(v);
            // Neutralise CSV formula injection. Raw exports carry free text
            // from Webcare (incident narratives, medicine names, goals), so a
            // cell starting with = + - @ tab or CR would be executed as a
            // formula by Excel/Sheets on open. Prefixing with an apostrophe
            // makes spreadsheets treat it as literal text.
            if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
            return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [headers.join(',')];
        for (const row of rows) {
            lines.push(headers.map(h => escape(row[h])).join(','));
        }
        return lines.join('\n');
    }

    private slug(name: string): string {
        return (
            (name || 'export')
                .toString()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '') || 'export'
        );
    }

    private downloadCsv(filename: string, csv: string): void {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}
